import { useCallback } from "react";
import { useDataSaveBatch } from "../useData";
import { hasBatchContent } from "../useData/batch";
import { useMainContext } from "../useMainContext";
import { draftMineOf, presentConflicts } from "./present";
import { useResolveConflicts } from "./hooks";
import { merge } from "./diff";
import { draftKey } from "./store";
import draft from "./instance";

// A Save told "Not saved" had gone through (its response lost)
const EARLIER_SAVED = "Saved: your earlier Save had gone through";

// save() sends the draft of the scope in one PUT /items (one commit) through
// useDataSaveBatch (refused on main, refresh of MemoryDB from the response).
// Resolves "OK" (the journal is cleared; or a 409 whose rows, fetched, are
// all a save of ours that had gone through, and nothing is left in the
// draft: its entries settled on them, "Saved: your earlier Save had gone
// through"), "KEPT" (the same 409, but the draft has changes left, made
// after that save: not saved, "...; your later changes are kept, save
// again"), "CONFLICT" (the conflict dialog is open, the draft stays), "KO"
// (error notified, the draft stays) or "EMPTY".
// The scope is saving (draft.isSaving) from the collect to the end of the
// request (clear, or saveSettled).
export default function useDraftSave(scope, { onSaved, onError } = {}) {
  // draftScope: the rows of this draft are sent; a row drafted in another
  // scope is refused
  const saveBatch = useDataSaveBatch({
    notify: "onError",
    conflictDialog: true,
    draftScope: scope
  });
  const { createNotification } = useMainContext();
  const resolveConflicts = useResolveConflicts();

  return useCallback(
    () =>
      new Promise((resolve) => {
        const batch = draft.collect(scope);
        if (!hasBatchContent(batch)) {
          draft.saveSettled(scope);
          resolve("EMPTY");
          return;
        }

        const count = batch.items.length + batch.deletes.length;

        saveBatch(
          batch,
          (status, responses) => {
            draft.clear(scope);
            createNotification(
              "success",
              `${count} change${count === 1 ? "" : "s"} saved`
            );
            onSaved && onSaved(responses);
            resolve("OK");
          },
          (status, data) => {
            draft.saveSettled(scope);
            if (data?.type !== "conflict") {
              resolve("KO");
              onError && onError(status, data);
              return;
            }

            const conflicts = data.conflicts ?? [];
            const opened = () => {
              resolve("CONFLICT");
              onError && onError(status, data);
            };
            // Resolves once the dialog is open (the stored rows fetched).
            // A conflict whose row, fetched, is a save of ours (Save clicked
            // again after "Not saved": the first had gone through, its
            // response lost) is left out of it: its entries are settled on
            // that row already (draft: OUR OWN SAVES). None left: no dialog;
            // saved when the draft has nothing left.
            presentConflicts({
              conflicts,
              // Each conflict in its own scope; the rest of the draft stays
              onDiscard: () => resolveConflicts(conflicts),
              // The rows of the preview (the dialog's items: their changes
              // do not overlap mine), on their version; the store merges
              // them again on what it has now
              onRebase: (items = []) =>
                draft.rebaseConflicts(
                  scope,
                  items.map(({ accessor, id }) => draftKey(accessor, id)),
                  merge
                ),
              mineOf: draftMineOf
            }).then((result) => {
              if (result?.shown !== false) {
                if (result?.own?.length)
                  createNotification("success", EARLIER_SAVED);
                opened();
                return;
              }
              // The 409 refused the whole request: what the draft has left
              // (changed after the Save that went through, or another row
              // changed since) is not saved
              if (draft.count(scope) > 0) {
                createNotification(
                  "success",
                  `${EARLIER_SAVED}; your later changes are kept, save again`
                );
                resolve("KEPT");
                onError && onError(status, data);
                return;
              }
              createNotification("success", EARLIER_SAVED);
              onSaved && onSaved();
              resolve("OK");
            }, opened);
          }
        );
      }),
    [createNotification, onError, onSaved, resolveConflicts, saveBatch, scope]
  );
}
